import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import lodashCompact from 'lodash/fp/compact';
import { useHistory } from 'react-router-dom';

import { numberWithCommas } from 'common/formatters';

import SrButton, {
  SR_BUTTON_HEIGHT_40,
  SR_BUTTON_FONT_SIZE_MICRO,
  SR_BUTTON_THEME_ADOBE_YELLOW,
  SR_BUTTON_THEME_ADOBE_GREEN,
} from 'client/components/SrButton/SrButton';
import UPResidualUtilityFaqModal from 'client/components/UPResidualUtilityFaqModal/UPResidualUtilityFaqModal';

import './UPResidualUtilityCard.less';

const UPResidualUtilityCard = ({
  utilityType,
  utilityProvider,
  utilityPartner,
  utilityActivation,
  defaultInternetAmenityPlan,
  completed = false,
}) => {
  const history = useHistory();

  const [isFaqModalOpen, setIsFaqModalOpen] = useState(false);

  const onClickChooseAnotherPlan = () => {
    const utilityActivationItem = utilityActivation.items.find((item) => {
      return item.utilityTypeId === utilityType.id;
    });

    const link = lodashCompact([
      '/utility-setup',
      utilityPartner.slug,
      utilityActivation.shortId,
      utilityType.label.toLowerCase(),
      'plan',
      completed && utilityActivationItem.utilityProviderId,
      completed && utilityActivationItem.utilityProviderPlanId,
    ]).join('/');

    history.push(link);
  };

  const getSelectedPlanText = () => {
    const utilityActivationItem = utilityActivation.items.find((item) => {
      return item.utilityTypeId === utilityType.id;
    });

    const selectedPlan = !utilityActivationItem.internetAmenityPlanId
      ? defaultInternetAmenityPlan
      : utilityProvider.plans.find((plan) => {
        return plan.internetAmenityPlanId === utilityActivationItem.internetAmenityPlanId;
      });

    return `Selected Plan: ${numberWithCommas(selectedPlan?.internetSpeedMbps)} Mbps`;
  };

  return (
    <div styleName={classNames('container', { completed })}>
      <div styleName="headerCard">
        <div styleName="headerIconBox" style={{ backgroundColor: utilityType.iconBackgroundColor }}>
          <div
            styleName="headerIconUrl"
            style={{ backgroundImage: `url(${utilityType.iconUrl})` }}
          />
        </div>
        <div styleName="titleBox">
          <span styleName={classNames('title', { completed })}>Internet included in home</span>
          <span styleName={classNames('subtitle', { completed })}>
            Required to use our internet plans.
          </span>
        </div>
        <div styleName="iconsBox">
          <div styleName="arrowIcon" />
          {completed && <div styleName="completedIcon" />}
        </div>
      </div>
      <div styleName="bodyCard">
        <div styleName={classNames('planBox', { completed })}>
          <div styleName="providerLogoBox">
            {utilityProvider?.logoUrl && (
              <div
                styleName="providerLogo"
                style={{ backgroundImage: `url(${utilityProvider.logoUrl})` }}
              />
            )}
            {defaultInternetAmenityPlan?.internetSpeedMbps && (
              <span styleName="selectedPlanText">{getSelectedPlanText()}</span>
            )}
          </div>
          <div styleName="benefitsBox">
            <div styleName="benefitItem">
              <span styleName="benefitIcon">🏡</span>
              <span styleName="benefitText orange" style={{ marginTop: '2px' }}>
                Included in your lease.
              </span>
            </div>
            <div styleName="benefitItem">
              <span styleName="benefitIcon">🛜</span>
              <span styleName="benefitText">Plug and play modem.</span>
            </div>
            <div styleName="benefitItem">
              <span styleName="benefitIcon">🏷️</span>
              <span styleName="benefitText">
                Exclusive discount
                <span styleName="benefitText weight400">&nbsp;from your landlord</span>
              </span>
            </div>
          </div>
          <div styleName="priceBox">
            <span styleName="priceText colorGreen">${defaultInternetAmenityPlan.price}</span>
            <span styleName="priceText size13 colorGreen">/mo for you. {'   '}</span>
            {defaultInternetAmenityPlan.originalPrice && (
              <>
                <span styleName="priceText colorGrey">
                  ${defaultInternetAmenityPlan.originalPrice}
                </span>
                <span styleName="priceText size13 colorGrey">/mo retail price.</span>
              </>
            )}
          </div>
          <SrButton
            hasBoxShadow
            theme={completed ? SR_BUTTON_THEME_ADOBE_GREEN : SR_BUTTON_THEME_ADOBE_YELLOW}
            heightType={SR_BUTTON_HEIGHT_40}
            fontSizeType={SR_BUTTON_FONT_SIZE_MICRO}
            onClick={onClickChooseAnotherPlan}
          >
            {completed ? 'View Internet Plan' : 'Schedule Equipment Delivery'}
          </SrButton>
        </div>
        <div styleName="howItWorksBox">
          <span onClick={() => setIsFaqModalOpen(true)}>How does this work with my lease?</span>
        </div>
      </div>
      {isFaqModalOpen && <UPResidualUtilityFaqModal onClose={() => setIsFaqModalOpen(false)} />}
    </div>
  );
};

UPResidualUtilityCard.displayName = 'UPResidualUtilityCard';

UPResidualUtilityCard.propTypes = {
  utilityType: PropTypes.object.isRequired,
  utilityProvider: PropTypes.object.isRequired,
  utilityPartner: PropTypes.object.isRequired,
  utilityActivation: PropTypes.object.isRequired,
  defaultInternetAmenityPlan: PropTypes.object.isRequired,
  completed: PropTypes.bool,
};

export default UPResidualUtilityCard;
